/* eslint-disable @typescript-eslint/no-explicit-any */
/** Opt-in, on-screen pointer-event tracer for diagnosing pen/stylus input on
 * devices without dev tools (iPad/tablets). It draws a small fixed overlay that
 * shows the last several pointer events and the code path each took, so a
 * "leave the writing area and come back" gesture can be inspected on the actual
 * hardware.
 *
 * Enable (then reload):  localStorage.setItem("gv:penDebug", "1")
 *   …or open the page with  ?penDebug=1
 * Disable:               localStorage.removeItem("gv:penDebug")
 *
 * Zero cost when disabled — penDebugEnabled() is cached and penLog() returns
 * immediately, so nothing is built or rendered in normal use. */

let cached: boolean | null = null;
let host: HTMLElement | null = null;
let lastAt = 0;
const lines: string[] = [];
const MAX_LINES = 16;

export function penDebugEnabled(): boolean {
  if (cached === null) {
    try {
      cached =
        (typeof localStorage !== "undefined" &&
          localStorage.getItem("gv:penDebug") === "1") ||
        (typeof location !== "undefined" && /[?&]penDebug=1\b/.test(location.search));
    } catch {
      cached = false;
    }
  }
  return cached;
}

function ensureHost(): HTMLElement | null {
  if (host || typeof document === "undefined" || !document.body) return host;
  const el = document.createElement("div");
  el.className = "gv-pen-debug";
  el.style.cssText =
    "position:fixed;top:6px;right:6px;z-index:99999;pointer-events:none;" +
    "max-width:62vw;max-height:48vh;overflow:hidden;white-space:pre;" +
    "font:10px/1.35 ui-monospace,SFMono-Regular,Menlo,monospace;color:#bfe9d4;" +
    "background:rgba(8,12,10,.84);border:1px solid #2a3a30;border-radius:6px;" +
    "padding:6px 8px;text-align:left;tab-size:2;";
  document.body.appendChild(el);
  host = el;
  return host;
}

/** Append one trace line (prefixed with ms since the previous event). */
export function penLog(msg: string): void {
  if (!penDebugEnabled()) return;
  const now = typeof performance !== "undefined" ? performance.now() : 0;
  const dt = lastAt ? Math.round(now - lastAt) : 0;
  lastAt = now;
  lines.push(`+${String(dt).padStart(4)}ms ${msg}`);
  if (lines.length > MAX_LINES) lines.shift();
  const el = ensureHost();
  if (el) el.textContent = lines.join("\n");
}

/** Compact one-line summary of a PointerEvent for the trace. `width` exposes
 * contact size (a palm is large, a stylus tip small) to tell them apart. */
export function fmtPtr(e: PointerEvent): string {
  const prim = e.isPrimary ? "*" : "";
  return `${e.pointerType}#${e.pointerId}${prim} b${e.buttons} p${e.pressure.toFixed(2)} w${Math.round(e.width)}`;
}
